"use server";

import { createHash, randomBytes } from "node:crypto";
import { cookies, headers } from "next/headers";
import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import {
  changePasswordSchema,
  forgotPasswordSchema,
  loginSchema,
  resetPasswordSchema,
  setPasswordSchema,
} from "@/lib/auth/schemas";
import { verifyPassword, DUMMY_PASSWORD_HASH, hashPassword } from "@/lib/auth/password";
import {
  PASSWORD_CHANGE_MAX_AGE_SECONDS,
  MFA_AUTH_MAX_AGE_SECONDS,
  signMfaChallengeToken,
  signMfaSetupToken,
  signPasswordChangeToken,
  signSessionToken,
  SESSION_COOKIE_NAME,
  SESSION_MAX_AGE_SECONDS,
  verifySessionToken,
} from "@/lib/auth/jwt";
import { getPlatformMfaState } from "@/lib/auth/platform-mfa";
import { consumeAuthRateLimit } from "@/lib/auth/rate-limit";
import { getCurrentUserContext } from "@/lib/auth/session";
import { withBypassRls } from "@/lib/db/scope";
import {
  isPasswordEmailConfigured,
  sendPasswordResetEmail,
} from "@/lib/email/password-reset";

export type LoginActionState = { error: string } | undefined;
export type AuthFormState =
  | { status: "error"; error: string; field?: string }
  | { status: "success"; message: string }
  | undefined;

type UserLoginRow = {
  id: string;
  email: string;
  password_hash: string;
  must_change_password: boolean;
  session_version: number;
};

function sessionCookieOptions(maxAge: number) {
  return {
    httpOnly: true,
    secure: process.env.NODE_ENV === "production",
    sameSite: "lax" as const,
    path: "/",
    maxAge,
  };
}

async function clientIp(): Promise<string> {
  const requestHeaders = await headers();
  return (
    requestHeaders.get("x-forwarded-for")?.split(",")[0]?.trim() ||
    requestHeaders.get("x-real-ip")?.trim() ||
    "unknown"
  );
}

function tokenHash(token: string): string {
  return createHash("sha256").update(token).digest("hex");
}

export async function login(
  _prevState: LoginActionState,
  formData: FormData
): Promise<LoginActionState> {
  const parsed = loginSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!parsed.success) return { error: "Перевірте правильність email та пароля." };

  const { email, password } = parsed.data;
  const allowed = await consumeAuthRateLimit(
    "login",
    [
      { kind: "email", value: email, maximum: 15 },
      { kind: "ip", value: await clientIp(), maximum: 50 },
    ],
    15
  );
  if (!allowed) return { error: "Забагато спроб входу. Спробуйте ще раз через 15 хвилин." };

  const user = await withBypassRls(async (client) => {
    const result = await client.query<UserLoginRow>(
      `select id, email, password_hash, must_change_password, session_version
       from users where email = $1`,
      [email]
    );
    return result.rows[0] ?? null;
  });

  const passwordValid = await verifyPassword(password, user?.password_hash ?? DUMMY_PASSWORD_HASH);
  if (!user || !passwordValid) return { error: "Невірний email або пароль." };

  const cookieStore = await cookies();
  if (user.must_change_password) {
    const token = await signPasswordChangeToken(user.id, user.session_version);
    cookieStore.set(
      SESSION_COOKIE_NAME,
      token,
      sessionCookieOptions(PASSWORD_CHANGE_MAX_AGE_SECONDS)
    );
    redirect("/change-password");
  }

  const mfaState = await getPlatformMfaState(user.id);
  if (mfaState.isPlatformAdmin) {
    const token = mfaState.enabled
      ? await signMfaChallengeToken(user.id, user.session_version)
      : await signMfaSetupToken(user.id, user.session_version);
    cookieStore.set(SESSION_COOKIE_NAME, token, sessionCookieOptions(MFA_AUTH_MAX_AGE_SECONDS));
    redirect(mfaState.enabled ? "/two-factor" : "/two-factor/setup");
  }

  const token = await signSessionToken(user.id, user.session_version);
  cookieStore.set(SESSION_COOKIE_NAME, token, sessionCookieOptions(SESSION_MAX_AGE_SECONDS));
  redirect("/");
}

export async function requestPasswordReset(
  _prevState: AuthFormState,
  formData: FormData
): Promise<AuthFormState> {
  const parsed = forgotPasswordSchema.safeParse({ email: formData.get("email") });
  if (!parsed.success) {
    return { status: "error", error: "Введіть коректний email.", field: "email" };
  }

  if (!isPasswordEmailConfigured()) {
    return {
      status: "error",
      error: "Надсилання листів ще не налаштоване. Зверніться до адміністратора кейтерингу.",
    };
  }

  const allowed = await consumeAuthRateLimit(
    "password_reset",
    [
      { kind: "email", value: parsed.data.email, maximum: 5 },
      { kind: "ip", value: await clientIp(), maximum: 20 },
    ],
    60
  );
  if (!allowed) {
    return { status: "error", error: "Забагато запитів. Спробуйте ще раз пізніше." };
  }

  const rawToken = randomBytes(32).toString("base64url");
  const recipient = await withBypassRls(async (client) => {
    const result = await client.query<{ id: string; email: string }>(
      "select id, email from users where email = $1",
      [parsed.data.email]
    );
    const user = result.rows[0];
    if (!user) return null;

    await client.query(
      "update password_reset_tokens set used_at = now() where user_id = $1 and used_at is null",
      [user.id]
    );
    await client.query(
      `insert into password_reset_tokens (user_id, token_hash, expires_at)
       values ($1, $2, now() + interval '60 minutes')`,
      [user.id, tokenHash(rawToken)]
    );
    return user.email;
  });

  if (recipient) {
    try {
      await sendPasswordResetEmail(recipient, rawToken);
    } catch (error) {
      console.error(
        "Password reset email delivery failed",
        error instanceof Error ? error.message : "unknown error"
      );
      await withBypassRls(async (client) => {
        await client.query(
          "update password_reset_tokens set used_at = now() where token_hash = $1",
          [tokenHash(rawToken)]
        );
      });
    }
  }

  return {
    status: "success",
    message: "Якщо обліковий запис із таким email існує, ми надіслали інструкцію для відновлення пароля.",
  };
}

export async function resetPassword(
  _prevState: AuthFormState,
  formData: FormData
): Promise<AuthFormState> {
  const parsed = resetPasswordSchema.safeParse({
    token: formData.get("token"),
    newPassword: formData.get("newPassword"),
    confirmPassword: formData.get("confirmPassword"),
  });
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      status: "error",
      error: issue?.message ?? "Перевірте введені дані.",
      field: issue?.path[0]?.toString(),
    };
  }

  const passwordHash = await hashPassword(parsed.data.newPassword);
  const changed = await withBypassRls(async (client) => {
    const result = await client.query<{ id: string; user_id: string }>(
      `select id, user_id from password_reset_tokens
       where token_hash = $1 and used_at is null and expires_at > now()
       for update`,
      [tokenHash(parsed.data.token)]
    );
    const resetToken = result.rows[0];
    if (!resetToken) return false;

    await client.query(
      `update users
       set password_hash = $1, must_change_password = false,
           session_version = session_version + 1, password_changed_at = now()
       where id = $2`,
      [passwordHash, resetToken.user_id]
    );
    await client.query(
      "update password_reset_tokens set used_at = now() where user_id = $1 and used_at is null",
      [resetToken.user_id]
    );
    return true;
  });

  if (!changed) {
    return {
      status: "error",
      error: "Посилання недійсне або строк його дії минув. Запросіть нове.",
    };
  }

  const cookieStore = await cookies();
  cookieStore.delete(SESSION_COOKIE_NAME);
  redirect("/login?password-reset=1");
}

export async function completeTemporaryPasswordChange(
  _prevState: AuthFormState,
  formData: FormData
): Promise<AuthFormState> {
  const parsed = setPasswordSchema.safeParse({
    newPassword: formData.get("newPassword"),
    confirmPassword: formData.get("confirmPassword"),
  });
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      status: "error",
      error: issue?.message ?? "Перевірте введені дані.",
      field: issue?.path[0]?.toString(),
    };
  }

  const cookieStore = await cookies();
  const identity = await verifySessionToken(cookieStore.get(SESSION_COOKIE_NAME)?.value ?? "");
  if (!identity || identity.purpose !== "password_change") {
    cookieStore.delete(SESSION_COOKIE_NAME);
    return {
      status: "error",
      error: "Сеанс зміни пароля завершився. Увійдіть тимчасовим паролем ще раз.",
    };
  }

  const passwordHash = await hashPassword(parsed.data.newPassword);
  const nextVersion = await withBypassRls(async (client) => {
    const result = await client.query<{ session_version: number; must_change_password: boolean }>(
      "select session_version, must_change_password from users where id = $1 for update",
      [identity.userId]
    );
    const user = result.rows[0];
    if (
      !user ||
      !user.must_change_password ||
      user.session_version !== identity.sessionVersion
    ) return null;

    const updated = await client.query<{ session_version: number }>(
      `update users
       set password_hash = $1, must_change_password = false,
           session_version = session_version + 1, password_changed_at = now()
       where id = $2 returning session_version`,
      [passwordHash, identity.userId]
    );
    return updated.rows[0]?.session_version ?? null;
  });

  if (!nextVersion) {
    cookieStore.delete(SESSION_COOKIE_NAME);
    return { status: "error", error: "Не вдалося оновити пароль. Увійдіть ще раз." };
  }

  const mfaState = await getPlatformMfaState(identity.userId);
  if (mfaState.isPlatformAdmin) {
    const token = mfaState.enabled
      ? await signMfaChallengeToken(identity.userId, nextVersion)
      : await signMfaSetupToken(identity.userId, nextVersion);
    cookieStore.set(SESSION_COOKIE_NAME, token, sessionCookieOptions(MFA_AUTH_MAX_AGE_SECONDS));
    redirect(mfaState.enabled ? "/two-factor" : "/two-factor/setup");
  }

  const token = await signSessionToken(identity.userId, nextVersion);
  cookieStore.set(SESSION_COOKIE_NAME, token, sessionCookieOptions(SESSION_MAX_AGE_SECONDS));
  redirect("/?password-created=1");
}

export async function changePassword(
  _prevState: AuthFormState,
  formData: FormData
): Promise<AuthFormState> {
  const parsed = changePasswordSchema.safeParse({
    currentPassword: formData.get("currentPassword"),
    newPassword: formData.get("newPassword"),
    confirmPassword: formData.get("confirmPassword"),
  });
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      status: "error",
      error: issue?.message ?? "Перевірте введені дані.",
      field: issue?.path[0]?.toString(),
    };
  }

  const context = await getCurrentUserContext();
  if (!context) return { status: "error", error: "Сеанс завершився. Увійдіть знову." };

  const newHash = await hashPassword(parsed.data.newPassword);
  const nextVersion = await withBypassRls(async (client) => {
    const row = (
      await client.query<{ password_hash: string }>(
        "select password_hash from users where id = $1 for update",
        [context.userId]
      )
    ).rows[0];
    if (!row || !(await verifyPassword(parsed.data.currentPassword, row.password_hash))) return null;

    const updated = await client.query<{ session_version: number }>(
      `update users set password_hash = $1, session_version = session_version + 1,
                        must_change_password = false, password_changed_at = now()
       where id = $2 returning session_version`,
      [newHash, context.userId]
    );
    await client.query(
      "update password_reset_tokens set used_at = now() where user_id = $1 and used_at is null",
      [context.userId]
    );
    return updated.rows[0]?.session_version ?? null;
  });

  if (!nextVersion) {
    return {
      status: "error",
      error: "Поточний пароль введено неправильно.",
      field: "currentPassword",
    };
  }

  const token = await signSessionToken(context.userId, nextVersion);
  const cookieStore = await cookies();
  cookieStore.set(SESSION_COOKIE_NAME, token, sessionCookieOptions(SESSION_MAX_AGE_SECONDS));
  revalidatePath("/portal/profile");
  revalidatePath("/platform/security");
  return {
    status: "success",
    message: "Пароль змінено. Сеанси на інших пристроях завершено.",
  };
}

export async function logoutOtherSessions(): Promise<void> {
  const context = await getCurrentUserContext();
  if (!context) redirect("/login");

  const nextVersion = await withBypassRls(async (client) => {
    const result = await client.query<{ session_version: number }>(
      "update users set session_version = session_version + 1 where id = $1 returning session_version",
      [context.userId]
    );
    return result.rows[0]?.session_version;
  });
  if (!nextVersion) redirect("/login");

  const token = await signSessionToken(context.userId, nextVersion);
  const cookieStore = await cookies();
  cookieStore.set(SESSION_COOKIE_NAME, token, sessionCookieOptions(SESSION_MAX_AGE_SECONDS));
  redirect("/portal/profile?sessions-ended=1");
}

export async function logout() {
  const cookieStore = await cookies();
  cookieStore.delete(SESSION_COOKIE_NAME);
  redirect("/login");
}
